import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, AlertTriangle, Loader, WifiOff } from 'lucide-react';
import useBackendHealth from '../hooks/useBackendHealth';

const BackendConnection = ({ onConnectionChange }) => {
  const [isVisible, setIsVisible] = useState(true);
  const { isOnline, isChecking, error, backendSource, backendLabel, activeBackendUrl, modelLoaded } = useBackendHealth();

  useEffect(() => {
    onConnectionChange?.(isOnline);

    if (isOnline) {
      const timer = setTimeout(() => setIsVisible(false), 3000);
      return () => clearTimeout(timer);
    }

    return undefined;
  }, [isOnline, onConnectionChange]);

  const getStatusConfig = () => {
    if (isChecking) {
      return {
        icon: Loader,
        color: 'text-blue-500',
        bgColor: 'bg-blue-500/10',
        borderColor: 'border-blue-500/30',
        title: 'Connecting to Backend',
        message: 'Checking the Render primary first, then Railway fallback if needed.',
        animation: 'animate-spin'
      };
    }

    if (isOnline && backendSource === 'railway') {
      return {
        icon: AlertTriangle,
        color: 'text-amber-500',
        bgColor: 'bg-amber-500/10',
        borderColor: 'border-amber-500/30',
        title: 'Railway Fallback Active',
        message: 'Render is still coming back online. Railway is serving requests for now and the app will switch back automatically.',
        animation: ''
      };
    }

    if (isOnline) {
      return {
        icon: CheckCircle,
        color: 'text-green-500',
        bgColor: 'bg-green-500/10',
        borderColor: 'border-green-500/30',
        title: 'Backend Connected',
        message: `Connected to ${backendLabel || 'the Render backend'} and ready for analytics.`,
        animation: ''
      };
    }

    return {
      icon: WifiOff,
      color: 'text-red-500',
      bgColor: 'bg-red-500/10',
      borderColor: 'border-red-500/30',
      title: 'Backend Unavailable',
      message: error || 'Unable to reach Render or the Railway fallback right now.',
      animation: ''
    };
  };

  const statusConfig = getStatusConfig();

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: -20 }}
        className={`fixed top-4 left-4 right-4 sm:left-auto z-50 sm:max-w-sm ${statusConfig.bgColor} backdrop-blur-xl border ${statusConfig.borderColor} rounded-2xl p-4 sm:p-6 shadow-2xl`}
      >
        <div className="flex items-start gap-4">
          <motion.div
            animate={isChecking ? { rotate: 360 } : {}}
            transition={{
              duration: 1,
              repeat: isChecking ? Infinity : 0,
              ease: 'linear'
            }}
            className={`flex-shrink-0 w-12 h-12 rounded-xl bg-slate-800/50 flex items-center justify-center ${statusConfig.color}`}
          >
            <statusConfig.icon size={24} className={statusConfig.animation} />
          </motion.div>

          <div className="flex-1 min-w-0">
            <h3 className={`font-bold text-lg ${statusConfig.color} mb-2`}>
              {statusConfig.title}
            </h3>
            <p className="text-slate-300 text-sm leading-relaxed mb-4">
              {statusConfig.message}
            </p>

            {isChecking && (
              <div className="flex items-center gap-2">
                <div className="flex gap-1">
                  {[0, 1, 2].map((i) => (
                    <motion.div
                      key={i}
                      className="w-2 h-2 bg-blue-500 rounded-full"
                      animate={{
                        scale: [1, 1.5, 1],
                        opacity: [0.5, 1, 0.5]
                      }}
                      transition={{
                        duration: 1.5,
                        repeat: Infinity,
                        delay: i * 0.2
                      }}
                    />
                  ))}
                </div>
                <span className="text-xs text-slate-400">Checking connection...</span>
              </div>
            )}

            {isOnline && backendSource === 'railway' && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: 'spring', damping: 15 }}
                className="flex items-center gap-2 text-amber-300"
              >
                <CheckCircle size={16} />
                <span className="text-xs font-medium">Fallback running on Railway</span>
              </motion.div>
            )}

            {isOnline && backendSource !== 'railway' && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: 'spring', damping: 15 }}
                className="flex items-center gap-2 text-green-400"
              >
                <CheckCircle size={16} />
                <span className="text-xs font-medium">Ready for analytics</span>
              </motion.div>
            )}
          </div>

          <button
            onClick={() => setIsVisible(false)}
            className="flex-shrink-0 w-8 h-8 rounded-lg bg-slate-700/50 hover:bg-slate-600/50 flex items-center justify-center text-slate-400 hover:text-white transition-all"
          >
            ×
          </button>
        </div>

        <div className="mt-4 pt-4 border-t border-white/5">
          <p className="text-[8px] font-black text-slate-500 uppercase tracking-widest">
            Active backend: {(activeBackendUrl || backendLabel || 'unknown').replace(/^https?:\/\//, '')}
          </p>
          {modelLoaded && (
            <p className="mt-1 text-[8px] font-black text-slate-600 uppercase tracking-widest">
              Models are live
            </p>
          )}
        </div>

        {isChecking && (
          <motion.div
            className="mt-4 h-1 bg-slate-700/50 rounded-full overflow-hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <motion.div
              className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full"
              initial={{ width: '0%' }}
              animate={{ width: '100%' }}
              transition={{ duration: 3, ease: 'easeInOut' }}
            />
          </motion.div>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export default BackendConnection;
